import { CSSProperties, RefObject, useCallback, useEffect, useRef, useState } from 'react'

/**
 * Where a horizontal scroller sits: whether it overflows at all, and whether
 * it is parked against its left or right end.
 *
 * One definition for every strip that scrolls sideways — the calendar and
 * streak-strip paging arrows read `atStart` / `atEnd` for their disabled
 * state, and the LLM-usage tables and the shortcut pill row read them for the
 * right-edge fade (`fadeRightStyle`). A pixel of slack at each end absorbs the
 * fractional `scrollLeft` a zoomed or high-DPI layout reports.
 *
 * Attach `ref` to the scrolling element. It is a callback ref so a scroller
 * that mounts late (after data, or behind a conditional) is still picked up;
 * `scrollRef` holds the same element for imperative use (scroll-to-end,
 * `scrollBy`), and `sync` re-measures after such a call.
 *
 * Re-measures on scroll, on a size change of the scroller or of any of its
 * direct children (a table filling in, chips animating in), and on children
 * being added or removed — the content growing inside a fixed-width box is
 * what turns overflow on, and the scroller's own size never changes for that.
 */
export interface HorizontalOverflow<T extends HTMLElement> {
  ref: (el: T | null) => void
  scrollRef: RefObject<T>
  /** The content is wider than the box (by more than a pixel). */
  overflow: boolean
  atStart: boolean
  atEnd: boolean
  sync: () => void
}

export function useHorizontalOverflow<T extends HTMLElement = HTMLDivElement>(): HorizontalOverflow<T> {
  const scrollRef = useRef<T | null>(null)
  const [element, setElement] = useState<T | null>(null)
  const [overflow, setOverflow] = useState(false)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  const sync = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setOverflow(max > 1)
    setAtStart(el.scrollLeft <= 1)
    setAtEnd(el.scrollLeft >= max - 1)
  }, [])

  const ref = useCallback((el: T | null) => {
    scrollRef.current = el
    setElement(el)
  }, [])

  useEffect(() => {
    if (!element) return
    sync()
    element.addEventListener('scroll', sync, { passive: true })

    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => sync())
    const observeAll = () => {
      if (!ro) return
      ro.disconnect()
      ro.observe(element)
      for (const child of Array.from(element.children)) ro.observe(child)
    }
    observeAll()

    const mo = typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver(() => { observeAll(); sync() })
    mo?.observe(element, { childList: true })

    return () => {
      element.removeEventListener('scroll', sync)
      ro?.disconnect()
      mo?.disconnect()
    }
  }, [element, sync])

  return { ref, scrollRef: scrollRef as RefObject<T>, overflow, atStart, atEnd, sync }
}

const FADE_WIDTH = '2rem'
const FADE_MASK = `linear-gradient(to right, #000 calc(100% - ${FADE_WIDTH}), transparent)`

/**
 * The "there is more to the right" hint: a mask that fades the scroller's
 * last `2rem` to transparent while it overflows and is not at its end.
 *
 * A mask rather than a gradient overlay so it needs no knowledge of the
 * background behind it (white card, gray page) and no wrapper element. The
 * mask is painted on the scroller's own box, so it stays at the visible right
 * edge however far the content is scrolled.
 */
export function fadeRightStyle(state: { overflow: boolean; atEnd: boolean }): CSSProperties | undefined {
  if (!state.overflow || state.atEnd) return undefined
  return { maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }
}
